import { AlertTriangle, DollarSign, Home, MapPin, ShieldCheck, Users, Handshake } from "lucide-react";
import Section from "./ContractSection";
import { useFormContext } from "react-hook-form";
import { FormCalendar, FormCheckbox, FormInput, FormSelect, FormTextarea, FormToggleGroup } from "@/components/forms/FormControls";
import { DealType } from "@/enum/common.enum";
import type { Deal } from "@/types/deals.types";
import {
  FINANCING_CONTINGENCY_OPTIONS,
  LOAN_TYPE_OPTIONS,
  PURCHASE_METHOD_OPTIONS,
} from "@/constant/deals-form.constant";

interface DealSummaryEditorFieldsProps {
  isCash: boolean;
  deal: Deal;
}

export default function DealSummaryEditorFields({ isCash, deal }: DealSummaryEditorFieldsProps) {
  const isSell = deal.deal_type === DealType.SELL;
  const isBuy = deal.deal_type === DealType.BUY;
  const form = useFormContext();
  const financingMode = form.watch("financing_contingency_mode");
  const homeSaleEnabled = form.watch("home_sale_contingency_enabled");
  const kickoutEnabled = form.watch("kickout_clause_enabled");
  const errors = form.formState.errors;
  const hasError = (fields: readonly string[]) => fields.some((f) => f in errors);

  return (
    <div className="space-y-3">
      <Section icon={DollarSign} title="Money & dates" description="Price, deposits, key dates and the inspection window." defaultOpen forceOpen={hasError(MONEY_FIELDS)}>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormInput name="purchase_price" label="Purchase price ($)" type="number" step="1" min="0" placeholder="750000" data-testid="ds-form-purchase-price" />
          <FormInput name="earnest_money_deposit" label="Earnest money ($)" type="number" step="1" min="0" placeholder="10000" data-testid="ds-form-emd" />
          <FormInput name="earnest_money_holder" label="Holder of earnest money" placeholder="e.g. Meridian Title Co." data-testid="ds-form-emd-holder" />
          <FormInput name="option_fee_amount" label="Option money / DD fee ($)" type="number" step="1" min="0" placeholder="2500" data-testid="ds-form-option-fee" />
          <FormCalendar name="earnest_money_due_date" label="Earnest money due date" data-testid="ds-form-emd-due" />
          <FormCalendar name="option_fee_due_date" label="DD money due date" data-testid="ds-form-option-fee-due" />
          <FormCalendar name="closing_date" label="Closing date" data-testid="ds-form-closing-date" />
          <FormCalendar name="due_diligence_end_date" label="Due diligence ends" data-testid="ds-form-dd-date" />
          <FormCalendar name="inspection_date" label="Inspection date" data-testid="ds-form-inspection-date" />
          <FormInput name="inspection_company" label="Inspection company" placeholder="e.g. BluePrint Home Inspections" data-testid="ds-form-inspection-company" />
          <FormInput name="inspection_start_time" label="Inspection window starts" type="time" data-testid="ds-form-inspection-start" />
          <FormInput name="inspection_end_time" label="Inspection window ends" type="time" data-testid="ds-form-inspection-end" />
        </div>
        <div className="mt-4">
          <FormTextarea name="inspection_notes" label="Inspection notes" rows={2} placeholder="Access instructions, scope, who will attend." data-testid="ds-form-inspection-notes" />
        </div>
      </Section>

      <Section icon={Users} title="Clients" description="The buyer and seller on this deal." defaultOpen forceOpen={hasError(CLIENT_FIELDS)}>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormInput name="seller_names" label="Name(s) of seller" placeholder="e.g. Jane & Robert Ellis" data-testid="ds-form-seller-names" />
          <FormInput name="buyer_names" label="Name(s) of buyer" placeholder="e.g. Michael & Joann Saar" data-testid="ds-form-buyer-names" />
        </div>
      </Section>

      <Section icon={Handshake} title="The other side" description="The agents and companies on each side of the deal — kept separate from your clients above." defaultOpen forceOpen={hasError(OTHER_SIDE_FIELDS)}>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormInput name="listing_agent" label="Listing agent" placeholder="e.g. Dana Cole" data-testid="ds-form-listing-agent" />
          <FormInput name="listing_brokerage" label="Listing brokerage / company" placeholder="e.g. Ridgeline Properties" data-testid="ds-form-listing-brokerage" />
          <FormInput name="coop_agent" label="Buyer's agent (co-op)" placeholder="e.g. Marcus Webb" data-testid="ds-form-coop-agent" />
          <FormInput name="coop_brokerage" label="Buyer's-agent brokerage" placeholder="e.g. Harbor & Vine Realty" data-testid="ds-form-coop-brokerage" />
        </div>
      </Section>

      <Section icon={ShieldCheck} title="Way of purchase" description="Cash or financed, loan type and the financing contingency." defaultOpen forceOpen={hasError(PURCHASE_FIELDS)}>
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormSelect name="purchase_method" label="Cash or financed" options={PURCHASE_METHOD_OPTIONS} data-testid="ds-form-purchase-method" />
          {!isCash && <FormSelect name="loan_type" label="Loan type" options={LOAN_TYPE_OPTIONS} data-testid="ds-form-loan-type" />}
          {!isCash && (
            <div className="sm:col-span-2" data-testid="ds-form-fin-block">
              <FormToggleGroup
                name="financing_contingency_mode"
                label="Financing contingency"
                options={FINANCING_CONTINGENCY_OPTIONS}
                className="justify-start"
                itemClassName="border border-edge bg-white px-2.5 py-1 text-[11px] uppercase tracking-[0.14em] data-[state=on]:bg-moss data-[state=on]:text-linen"
              />
              <div className="mt-3">
                {financingMode === "days" ? (
                  <FormInput name="financing_contingency_days" label="Days from contract" type="number" min="0" placeholder="e.g. 21" data-testid="ds-form-fin-days" />
                ) : (
                  <FormCalendar name="financing_contingency_date" label="Specific date" data-testid="ds-form-fin-date" />
                )}
              </div>
            </div>
          )}
        </div>
      </Section>

      <Section icon={Home} title="Contingencies" description="Home-sale contingency and kickout clause." defaultOpen forceOpen={hasError(CONTINGENCY_FIELDS)}>
        <div className="space-y-3">
        <div className="rounded-lg border border-edge bg-linen/60 p-3.5">
        <FormCheckbox name="home_sale_contingency_enabled" label="Home-sale contingency" description="Buyer's purchase depends on selling another property." data-testid="ds-form-hsc-check" />
        {homeSaleEnabled && <div className="ml-7"><FormInput name="home_sale_contingency_address" label="Address of property that must be sold" placeholder="123 Main St, City, ST" data-testid="ds-form-hsc-address" /></div>}
        </div>
        <div className="rounded-lg border border-edge bg-linen/60 p-3.5">
        <FormCheckbox name="kickout_clause_enabled" label="Kickout clause" description="Seller can accept another offer if buyer doesn't remove contingency in time." data-testid="ds-form-kickout-check" />
        {kickoutEnabled && <div className="ml-7"><FormTextarea name="kickout_clause_details" label="Details (notice window, backup terms)" rows={2} data-testid="ds-form-kickout-details" /></div>}
        </div>
        </div>
      </Section>

      <Section icon={AlertTriangle} title="Special stipulations" description="Stipulations and anything else worth recording." defaultOpen forceOpen={hasError(NOTES_FIELDS)}>
        <FormTextarea name="special_stipulations" label="Special stipulations" rows={4} placeholder="e.g. Seller to convey washer/dryer. Home warranty at closing paid by seller." data-testid="ds-form-stipulations" />
        <div className="mt-4">
          <FormTextarea name="further_considerations" label="Any further considerations to note" rows={4} placeholder="Anything else worth recording about this contract." data-testid="ds-form-further" />
        </div>
      </Section>

      <Section icon={MapPin} title="Closing day" description="Where and when the closing happens, and who to contact." defaultOpen forceOpen={hasError(CLOSING_FIELDS)}>
        <div className="space-y-4">
        <FormInput name="closing_law_firm" label="Closing law firm / title company" placeholder="e.g. Ellison & Park LLP" data-testid="ds-form-closing-law-firm" />
        <FormInput name="closing_address" label="Closing address (title company / attorney office)" placeholder="e.g. 500 Main St, Suite 300, Brooklyn, NY 11201" data-testid="ds-form-closing-address" />
        <div className="grid gap-x-5 gap-y-4 sm:grid-cols-2">
          <FormInput name="closing_datetime" label="Date & time" type="datetime-local" data-testid="ds-form-closing-datetime" />
          <FormInput name="possession_datetime" label="Possession date & time" type="datetime-local" data-testid="ds-form-possession" />
          {isBuy && <FormCalendar name="utility_connect_date" label="Utility connect date" data-testid="ds-form-utility-connect" />}
          {isSell && <FormCalendar name="utility_disconnect_date" label="Utility disconnect date" data-testid="ds-form-utility-disconnect" />}
          <FormInput name="closing_contact_name" label="Contact name" placeholder="e.g. Sarah at Meridian Title" data-testid="ds-form-closing-contact-name" />
          <div className="sm:col-span-2"><FormInput name="closing_contact_phone" label="Contact phone" type="tel" placeholder="e.g. (718) 555-0134" data-testid="ds-form-closing-contact-phone" /></div>
        </div>
        </div>
      </Section>
    </div>
  );
}

const MONEY_FIELDS = [
  "purchase_price", "earnest_money_deposit", "earnest_money_holder", "option_fee_amount", "earnest_money_due_date", "option_fee_due_date", "closing_date", "due_diligence_end_date",
  "inspection_date", "inspection_company", "inspection_start_time", "inspection_end_time", "inspection_notes",
] as const;
const CLIENT_FIELDS = ["seller_names", "buyer_names"] as const;
const OTHER_SIDE_FIELDS = ["listing_agent", "listing_brokerage", "coop_agent", "coop_brokerage"] as const;
const PURCHASE_FIELDS = ["purchase_method", "loan_type", "financing_contingency_mode", "financing_contingency_days", "financing_contingency_date"] as const;
const CONTINGENCY_FIELDS = ["home_sale_contingency_enabled", "home_sale_contingency_address", "kickout_clause_enabled", "kickout_clause_details"] as const;
const NOTES_FIELDS = ["special_stipulations", "further_considerations"] as const;
const CLOSING_FIELDS = [
  "closing_law_firm", "closing_address", "closing_datetime", "possession_datetime", "utility_connect_date", "utility_disconnect_date",
  "closing_contact_name", "closing_contact_phone",
] as const;
